/**
 * NIfTI mask loader — downloads a gzipped NIfTI-1 file from a presigned URL
 * and writes the segment indices into a Cornerstone segmentation volume.
 *
 * NIfTI-1 format: 348-byte header + optional extension + raw voxel data.
 * We only need the datatype and dim fields from the header to interpret the buffer.
 */

// pako 3.x ESM exposes named exports only (no default) — namespace import.
import * as pako from "pako";
import { getCornerstone } from "./dicomLoader";
import { extractContourEdges, chaikinSmooth } from "./contourUtils";
import { projectIppDeltaOntoNormal } from "./sliceGeometry";
import { ensureContourSegmentationActive, getContourSegmentationId } from "./viewportToolSetup";
import { collectMaskSegmentIndices, initContourSegmentIndices } from "./niftiMaskSegmentInit";
import { LOADED_FROM_MASK_KEY } from "./annotationFlags";
import { getDefaultStore } from "jotai";
import { studyMetadataAtom } from "@/atoms/selectionAtoms";
import { buildS3ImageIds } from "@/utils/dicomMetadataLoader";
import {
  createSavedMaskSampler,
  isLegacyMaskAffine,
  isStackEpsilonAffine,
  validateSavedMaskAffine,
  volumeSlicePlane,
} from "@/utils/savedMaskSampling";
import { normalizeLegacyStackMask, stackSliceFrame } from "@/utils/stackSliceGeometry";
import type { SavedStoneOrder } from "@/utils/savedStoneOrder";

/** The series these images belong to, in its listed (instance) order: the order
 *  a stack shows it, and so the slice order of a pre-ENG-258 stack save. */
function seriesListImageIds(imageIds: string[]): string[] | undefined {
  const metadata = getDefaultStore().get(studyMetadataAtom);
  const cdn = metadata?.canonical_cdn_base_url;
  if (!cdn || !imageIds.length) return undefined;
  const wanted = new Set(imageIds);
  for (const instances of Object.values(metadata.series ?? {})) {
    if (instances?.length !== imageIds.length) continue;
    const listed = buildS3ImageIds(cdn, instances);
    if (listed.every((id) => wanted.has(id))) return listed;
  }
  return undefined;
}

// NIfTI-1 header field offsets (little-endian)
const SIZEOF_HDR = 0; // int32 at byte 0 — must be 348
const DIM_OFFSET = 40; // int16[8] starting at byte 40
const DATATYPE_OFFSET = 70; // int16 at byte 70
const VOXOFFSET = 108; // float32 at byte 108 — offset to voxel data
// sform — 4x4 affine encoded as 3 rows of 4 floats. 4th row is implicit (0,0,0,1).
const SFORM_CODE_OFFSET = 254; // int16
const SROW_X_OFFSET = 280; // float32[4]
const SROW_Y_OFFSET = 296; // float32[4]
const SROW_Z_OFFSET = 312; // float32[4]

// NIfTI datatype codes we support
const DT_UINT8 = 2;
const DT_UINT16 = 512;
const DT_INT16 = 4;
const DT_FLOAT32 = 16;

/**
 * Maximum caliper (Feret) diameter in mm of the segmentation in a NIfTI mask —
 * the longest straight-line distance between any two mask voxels. This is the
 * true longest dimension; the convex-hull `axis_1_mm` saved on the annotation
 * runs ~10-20% larger because the hull is convex and wraps the drawn contour.
 *
 * Distances use the NIfTI sform affine's 3x3 linear part (voxel delta -> world
 * mm), so anisotropic/oblique spacing is handled exactly. Only boundary voxels
 * can be a caliper endpoint, so we reduce to those first, then brute-force the
 * max pairwise distance (downsampled past a cap — stones rarely trip it).
 * Returns null on parse failure or an empty mask.
 */
export async function computeMaxCaliperMm(maskUrl: string): Promise<number | null> {
  const { getApiBaseUrl } = await import("../config/api");
  const proxyUrl = `${getApiBaseUrl()}/api/labeling/proxy-mask?url=${encodeURIComponent(maskUrl)}`;
  const response = await fetch(proxyUrl);
  if (!response.ok) return null;

  const decompressed = pako.inflate(new Uint8Array(await response.arrayBuffer()));
  const view = new DataView(decompressed.buffer);
  if (view.getInt32(SIZEOF_HDR, true) !== 348) return null;

  const cols = view.getInt16(DIM_OFFSET + 2, true);
  const rows = view.getInt16(DIM_OFFSET + 4, true);
  const slices = view.getInt16(DIM_OFFSET + 6, true);
  const datatype = view.getInt16(DATATYPE_OFFSET, true);
  const total = cols * rows * slices;
  if (total <= 0) return null;
  const voxStart = Math.round(Math.max(view.getFloat32(VOXOFFSET, true), 352));

  // sform 3x3 linear part: maps a voxel-index delta to a world (mm) delta.
  const m = [
    [
      view.getFloat32(SROW_X_OFFSET, true),
      view.getFloat32(SROW_X_OFFSET + 4, true),
      view.getFloat32(SROW_X_OFFSET + 8, true),
    ],
    [
      view.getFloat32(SROW_Y_OFFSET, true),
      view.getFloat32(SROW_Y_OFFSET + 4, true),
      view.getFloat32(SROW_Y_OFFSET + 8, true),
    ],
    [
      view.getFloat32(SROW_Z_OFFSET, true),
      view.getFloat32(SROW_Z_OFFSET + 4, true),
      view.getFloat32(SROW_Z_OFFSET + 8, true),
    ],
  ];
  // Fall back to diagonal pixdim if sform is absent/degenerate.
  if (!m.some((r) => r.some((v) => v !== 0))) {
    const sx = view.getFloat32(80, true) || 1;
    const sy = view.getFloat32(84, true) || 1;
    const sz = view.getFloat32(88, true) || 1;
    m[0] = [sx, 0, 0];
    m[1] = [0, sy, 0];
    m[2] = [0, 0, sz];
  }

  let val: (idx: number) => number;
  if (datatype === DT_UINT16) {
    const a = new Uint16Array(decompressed.buffer, voxStart, total);
    val = (i) => a[i];
  } else if (datatype === DT_UINT8) {
    const a = new Uint8Array(decompressed.buffer, voxStart, total);
    val = (i) => a[i];
  } else if (datatype === DT_INT16) {
    const a = new Int16Array(decompressed.buffer, voxStart, total);
    val = (i) => a[i];
  } else if (datatype === DT_FLOAT32) {
    const a = new Float32Array(decompressed.buffer, voxStart, total);
    val = (i) => a[i];
  } else {
    return null;
  }

  const at = (i: number, j: number, k: number) => val(i + j * cols + k * cols * rows);
  const bx: number[] = [];
  const by: number[] = [];
  const bz: number[] = [];
  for (let k = 0; k < slices; k++) {
    for (let j = 0; j < rows; j++) {
      for (let i = 0; i < cols; i++) {
        if (at(i, j, k) <= 0) continue;
        const isBoundary =
          i === 0 ||
          i === cols - 1 ||
          j === 0 ||
          j === rows - 1 ||
          k === 0 ||
          k === slices - 1 ||
          at(i - 1, j, k) <= 0 ||
          at(i + 1, j, k) <= 0 ||
          at(i, j - 1, k) <= 0 ||
          at(i, j + 1, k) <= 0 ||
          at(i, j, k - 1) <= 0 ||
          at(i, j, k + 1) <= 0;
        if (isBoundary) {
          bx.push(i);
          by.push(j);
          bz.push(k);
        }
      }
    }
  }

  const n = bx.length;
  if (n < 2) return null;
  const CAP = 4000;
  const step = n > CAP ? Math.ceil(n / CAP) : 1;

  let maxSq = 0;
  for (let a = 0; a < n; a += step) {
    for (let b = a + step; b < n; b += step) {
      const di = bx[a] - bx[b];
      const dj = by[a] - by[b];
      const dk = bz[a] - bz[b];
      const wx = m[0][0] * di + m[0][1] * dj + m[0][2] * dk;
      const wy = m[1][0] * di + m[1][1] * dj + m[1][2] * dk;
      const wz = m[2][0] * di + m[2][1] * dj + m[2][2] * dk;
      const d2 = wx * wx + wy * wy + wz * wz;
      if (d2 > maxSq) maxSq = d2;
    }
  }
  return Math.sqrt(maxSq);
}

// Contour extraction and smoothing imported from contourUtils.ts

/**
 * Load a NIfTI mask and convert each slice's segments into contour annotations.
 *
 * Used by the contour-based segmentation workflow where there is no 3D volume
 * to write into. Instead, we extract the boundary contour for each slice and
 * create PlanarFreehandContourSegmentationTool annotations.
 */
export async function loadMaskAsContours(
  maskUrl: string,
  segmentationId: string,
  viewport: any,
  element: HTMLElement,
  options?: {
    annotationIdsBySegment?: ReadonlyMap<number, string>;
    localIndicesByStoredSegment?: ReadonlyMap<number, number>;
    signal?: AbortSignal;
    skipExistingContours?: boolean;
    /** Opt-in subset for incremental saved-annotation loading from a shared mask. */
    includedStoredSegmentIndices?: ReadonlySet<number>;
    /** The series in its listed (instance) order, for legacy 2D-mode masks.
     *  Defaults to the study metadata's list for the viewport's images. */
    listImageIds?: string[];
    /** Stored label → the slice order its record says it was saved in (ENG-258,
     *  `savedStoneOrder`). Lets a legacy file that mixes stack and volume saves
     *  put each stone on its own image. */
    labelOrders?: ReadonlyMap<number, SavedStoneOrder>;
  },
): Promise<number> {
  const { getApiBaseUrl } = await import("../config/api");
  const proxyUrl = `${getApiBaseUrl()}/api/labeling/proxy-mask?url=${encodeURIComponent(maskUrl)}`;
  const response = await fetch(proxyUrl, { signal: options?.signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch mask: ${response.status} ${response.statusText}`);
  }
  const compressedBuf = await response.arrayBuffer();

  const decompressed = pako.inflate(new Uint8Array(compressedBuf));
  const dvw = new DataView(decompressed.buffer);

  if (dvw.byteLength < 352) throw new Error("Truncated NIfTI mask header");
  const hdrSize = dvw.getInt32(SIZEOF_HDR, true);
  if (hdrSize !== 348) throw new Error(`Invalid NIfTI header: ${hdrSize}`);

  const cols = dvw.getInt16(DIM_OFFSET + 2, true);
  const rows = dvw.getInt16(DIM_OFFSET + 4, true);
  const slices = dvw.getInt16(DIM_OFFSET + 6, true);
  const totalVoxels = cols * rows * slices;
  const datatype = dvw.getInt16(DATATYPE_OFFSET, true);
  const voxOffset = Math.max(dvw.getFloat32(VOXOFFSET, true), 352);
  const voxelStart = Math.round(voxOffset);

  const bytesPerVoxel = ({ 2: 1, 512: 2, 4: 2, 16: 4 } as Record<number, number>)[datatype];
  if (
    [cols, rows, slices].some((v) => v <= 0) ||
    !Number.isSafeInteger(voxelStart) ||
    !bytesPerVoxel ||
    voxelStart % bytesPerVoxel !== 0 ||
    voxelStart + totalVoxels * bytesPerVoxel > decompressed.byteLength
  ) {
    throw new Error("Unsupported or truncated NIfTI mask data");
  }

  let maskData: Uint16Array;
  if (datatype === DT_UINT16) {
    maskData = new Uint16Array(decompressed.buffer, voxelStart, totalVoxels);
  } else if (datatype === DT_UINT8) {
    const raw = new Uint8Array(decompressed.buffer, voxelStart, totalVoxels);
    maskData = new Uint16Array(totalVoxels);
    for (let i = 0; i < totalVoxels; i++) maskData[i] = raw[i];
  } else if (datatype === DT_INT16) {
    const raw = new Int16Array(decompressed.buffer, voxelStart, totalVoxels);
    maskData = new Uint16Array(totalVoxels);
    for (let i = 0; i < totalVoxels; i++) maskData[i] = Math.max(0, raw[i]);
  } else if (datatype === DT_FLOAT32) {
    const raw = new Float32Array(decompressed.buffer, voxelStart, totalVoxels);
    maskData = new Uint16Array(totalVoxels);
    for (let i = 0; i < totalVoxels; i++) maskData[i] = Math.round(Math.max(0, raw[i]));
  } else {
    throw new Error(`Unsupported NIfTI datatype: ${datatype}`);
  }

  const cornerstone = getCornerstone();
  const { getCornerstoneTools } = await import("./dicomLoader");
  options?.signal?.throwIfAborted();
  const csTools = getCornerstoneTools();
  const csUtils = cornerstone.utilities;
  const annotationState = csTools.annotation.state;

  // Backend creates (rows, cols, slices), serialized in Fortran order.
  // dim[1] = rows, dim[2] = cols from numpy shape (rows, cols, slices).
  const nRows = cols; // dim[1] = first numpy axis = rows
  const nCols = rows; // dim[2] = second numpy axis = cols

  /** Read a voxel from the NIfTI flat data (Fortran/column-major order).
   *  nibabel writes numpy arrays in Fortran order: first axis varies fastest.
   *  For mask shape (rows, cols, slices): flat = s*(rows*cols) + c*rows + r */
  function readVoxel(r: number, c: number, s: number): number {
    return maskData[s * (nRows * nCols) + c * nRows + r];
  }

  const imageData = viewport.getImageData?.();
  const vtkImageData = imageData?.imageData;

  const imageIds: string[] = viewport.getImageIds?.() ?? [];
  const camera = viewport.getCamera?.() ?? {};
  const viewPlaneNormal = camera.viewPlaneNormal ?? [0, 0, 1];
  const viewUp = camera.viewUp ?? [0, -1, 0];
  const frameOfRef = viewport.getFrameOfReferenceUID?.();

  // The backend keeps its column/row file affine convention. The shared card
  // sampler inverts it and reads row-first bytes. Historical diagonal/zero-origin
  // files remain on the legacy path; the file alone cannot identify their grid.
  const sformCode = dvw.getInt16(SFORM_CODE_OFFSET, true);
  const srowX = [
    dvw.getFloat32(SROW_X_OFFSET, true),
    dvw.getFloat32(SROW_X_OFFSET + 4, true),
    dvw.getFloat32(SROW_X_OFFSET + 8, true),
    dvw.getFloat32(SROW_X_OFFSET + 12, true),
  ];
  const srowY = [
    dvw.getFloat32(SROW_Y_OFFSET, true),
    dvw.getFloat32(SROW_Y_OFFSET + 4, true),
    dvw.getFloat32(SROW_Y_OFFSET + 8, true),
    dvw.getFloat32(SROW_Y_OFFSET + 12, true),
  ];
  const srowZ = [
    dvw.getFloat32(SROW_Z_OFFSET, true),
    dvw.getFloat32(SROW_Z_OFFSET + 4, true),
    dvw.getFloat32(SROW_Z_OFFSET + 8, true),
    dvw.getFloat32(SROW_Z_OFFSET + 12, true),
  ];
  let affine = [...srowX, ...srowY, ...srowZ];
  if (sformCode > 0) validateSavedMaskAffine(affine);
  if (!vtkImageData) throw new Error("No VTK image data for saved mask loading");
  // ENG-258: a mask saved from a stack before the fix has a 0.001 mm slice axis
  // and the on-screen slice as origin; its voxels are right by the stack's list
  // index. Rebuild what a save writes now (source volume header, volume slice
  // order), then read it like any other mask. Refuse rather than guess.
  // Old merges kept stones from both modes in one file under the last save's
  // header, so a record that says `list` also triggers this on a normal header,
  // and each label moves by its own record (no record: the header's default).
  const labelOrders = options?.labelOrders;
  const stackHeader = sformCode > 0 && isStackEpsilonAffine(affine);
  const listLabelUnderNormalHeader =
    !stackHeader &&
    sformCode > 0 &&
    !isLegacyMaskAffine(affine) &&
    [...(labelOrders?.values() ?? [])].includes("list");
  if (stackHeader || listLabelUnderNormalHeader) {
    // A stack lists the series in that order itself; a volume sorts it.
    const listImageIds =
      options?.listImageIds ??
      seriesListImageIds(imageIds) ??
      (viewport.type === "stack" ? imageIds : undefined);
    const [imageColumns, imageRows] = vtkImageData.getDimensions?.() ?? [];
    const fits =
      !!listImageIds &&
      imageColumns === nCols &&
      imageRows === nRows &&
      slices === listImageIds.length;
    if (!fits && stackHeader) throw new Error("Saved 2D-mode mask doesn't match its series");
    if (fits) {
      const unknown = [...(labelOrders ?? [])].filter(([, o]) => o === "unknown");
      if (unknown.length) {
        console.warn("[niftiMaskLoader] saved mode unknown, header default used (ENG-258)", {
          labels: unknown.map(([label]) => label),
        });
      }
      ({ data: maskData, affine } = normalizeLegacyStackMask({
        data: maskData,
        rows: nRows,
        columns: nCols,
        slices,
        listImageIds: listImageIds!,
        getVolumes: () => cornerstone.cache?.getVolumes?.() ?? [],
        orderOfLabel: (label) => {
          const order = labelOrders?.get(label);
          return order === "unknown" ? undefined : order;
        },
        defaultOrder: stackHeader ? "list" : "volume",
      }));
      console.warn("[niftiMaskLoader] legacy 2D-mode mask read by slice index (ENG-258)");
    } else {
      // A normal header still reads on the normal path; only the stack stones may be off.
      console.warn("[niftiMaskLoader] 2D-mode stones can't be reindexed: grid differs (ENG-258)");
    }
  }
  const useSformAffine = sformCode > 0 && !isLegacyMaskAffine(affine);
  // Validate every target before registering any contours (no partial geometry on error).
  if (useSformAffine && !imageIds.length) throw new Error("No CT images for saved mask loading");
  // Sample in the volume frame Save used, not per-image metadata that changes as
  // slices stream (sidecar IPP can be synthesized; streamed DICOM IPP is real).
  // A stack's vtkImageData is one image, so it samples in its source volume's
  // frame, the one stack Saves write (ENG-258).
  const isStack = viewport.type === "stack";
  const sliceFrame =
    useSformAffine && isStack
      ? stackSliceFrame(viewport, () => cornerstone.cache?.getVolumes?.() ?? [])
      : vtkImageData;
  if (useSformAffine && sliceFrame.getDimensions?.()?.[2] !== imageIds.length)
    throw new Error("CT volume does not match its images");
  // Viewport slice s reads file slice toFileSlice(s): the stack's list may run
  // opposite the volume (file) order. Identity on a volume viewport.
  const toFileSlice: (slice: number) => number =
    "toFileSlice" in sliceFrame && typeof sliceFrame.toFileSlice === "function"
      ? sliceFrame.toFileSlice
      : (slice) => slice;
  const targetPlanes = useSformAffine
    ? imageIds.map((_, s) => volumeSlicePlane(sliceFrame, toFileSlice(s)))
    : [];
  const sampleMask = useSformAffine
    ? createSavedMaskSampler(
        maskData,
        { rows: nRows, columns: nCols },
        affine,
        options?.includedStoredSegmentIndices,
      )
    : undefined;
  // Log which world-coord path is taken so geometry regressions on legacy
  // saves vs. new oriented saves can be diagnosed from the console.
  console.debug(
    `[niftiMaskLoader] sform path: ${useSformAffine ? "oriented affine" : "legacy IPP-delta"} ` +
      `(sformCode=${sformCode})`,
  );

  let totalContours = 0;

  // Count non-zero voxels and find which slices have data
  const slicesWithData: number[] = [];
  for (let s = 0; s < slices; s++) {
    let sliceNonZero = 0;
    for (let r = 0; r < nRows; r++) {
      for (let c = 0; c < nCols; c++) {
        if (readVoxel(r, c, s) > 0) sliceNonZero++;
      }
    }
    if (sliceNonZero > 0) {
      slicesWithData.push(s);
    }
  }

  // Pre-compute per-slice world coordinate transform from imagePlaneModule.
  // This avoids viewport navigation timing issues — we read DICOM metadata
  // directly for each slice's imageId.
  //
  // The save flow does:
  //   worldToCanvas(worldPt) → canvasPt
  //   canvasToIndex(canvasPt) → [colIdx, rowIdx]   (Cornerstone index = [x, y])
  //   stores as [row, col] = [rowIdx, colIdx]
  //
  // To reverse: given [row, col], compute world position using:
  //   world = IPP + col * rowCosines * colSpacing + row * colCosines * rowSpacing
  //
  // DICOM definitions:
  //   rowCosines = direction cosine of the first row (direction cols increase)
  //   columnCosines = direction cosine of the first column (direction rows increase)
  //   rowPixelSpacing = spacing between rows (distance along columnCosines)
  //   columnPixelSpacing = spacing between columns (distance along rowCosines)

  // Cache the current vtkImageData origin (IPP of currently displayed slice).
  // We use this to compute the offset to each target slice's origin WITHOUT
  // navigating the viewport (which corrupts viewport state for annotation tools).
  const currentOrigin = vtkImageData?.getOrigin() ?? [0, 0, 0];

  // Make the paired __contour segmentation the active one on every viewport
  // *before* we write any annotations. The annotation binding below uses
  // getContourSegmentationId(segmentationId), so we must guarantee that id
  // is the active contour rep on all viewports — otherwise cornerstone's
  // renderer walks a different segmentation and our writes are orphaned.
  // ensureContourSegmentationActive is idempotent.
  //
  // Every active-segment write below (both ensure calls and the per-index init)
  // keeps the index that was active before the load. Without that, the load
  // left the contour seg on segment 1 while the panel's atom said N, so the
  // next stroke got the wrong index and Save counted 0 slices (ENG-248).
  const contourSegId = getContourSegmentationId(segmentationId);
  const activeSegmentBeforeLoad =
    csTools.segmentation.segmentIndex?.getActiveSegmentIndex?.(contourSegId) ?? 1;
  try {
    ensureContourSegmentationActive(segmentationId, activeSegmentBeforeLoad);
  } catch (err) {
    console.warn("[niftiMaskLoader] ensureContourSegmentationActive failed:", err);
  }

  // Ensure segmentation representation exists before the contour loop.
  // If it's missing (slow viewport init), create it once here rather than
  // retrying per-contour inside the loop.
  try {
    const { segmentation, Enums: ToolEnums } = csTools;
    const stateManager = (segmentation as any).defaultSegmentationStateManager;
    if (!stateManager?.getActiveSegmentation(viewport.id)) {
      const existingSegs: any[] = segmentation.state?.getSegmentations?.() ?? [];
      if (!existingSegs.some((s: any) => s.segmentationId === segmentationId)) {
        segmentation.addSegmentations([
          {
            segmentationId,
            representation: { type: ToolEnums.SegmentationRepresentations.Contour },
          },
        ]);
      }
      segmentation.addSegmentationRepresentations(viewport.id, [
        {
          segmentationId,
          type: ToolEnums.SegmentationRepresentations.Contour,
        },
      ]);
      segmentation.activeSegmentation.setActiveSegmentation(viewport.id, segmentationId);
    }
  } catch (err) {
    console.warn("[niftiMaskLoader] Pre-loop segmentation ensure failed:", err);
  }

  // #685: a multi-stone mask writes several segment indices, but only index 1
  // is lazily initialized during setup. addContourSegmentationAnnotation reads
  // segments[idx].locked and TypeErrors on the missing 2nd+ entry — the
  // annotation then never registers with the contour rep (invisible mask, dead
  // lasso/bump, per-slice exception flood on load). Pre-create every index the
  // mask uses on the SAME contour seg the annotations bind to below
  // (getContourSegmentationId), before the add-loop. Registering an index makes
  // it active, so the index active before the load is restored right after.
  const maskSegmentIndices = collectMaskSegmentIndices(
    readVoxel,
    slicesWithData,
    nRows,
    nCols,
  ).filter(
    (index) =>
      !options?.includedStoredSegmentIndices || options.includedStoredSegmentIndices.has(index),
  );
  function resolveLocalIndex(storedIndex: number): number {
    if (!options?.localIndicesByStoredSegment) return storedIndex;
    const localIndex = options.localIndicesByStoredSegment.get(storedIndex);
    if (localIndex === undefined || !Number.isSafeInteger(localIndex) || localIndex <= 0) {
      throw new Error(`Saved mask label ${storedIndex} has no valid viewer index`);
    }
    return localIndex;
  }
  // Resolve every label before registering any geometry; never fall back to a
  // backend label when this caller requested viewer-local remapping.
  const localIndices = maskSegmentIndices.map(resolveLocalIndex);
  if (new Set(localIndices).size !== localIndices.length) {
    throw new Error("Saved mask labels map to the same viewer index");
  }
  initContourSegmentIndices(csTools.segmentation, contourSegId, localIndices);
  csTools.segmentation.segmentIndex?.setActiveSegmentIndex?.(contourSegId, activeSegmentBeforeLoad);

  const displaySlices = useSformAffine ? imageIds.map((_, i) => i) : slicesWithData;
  for (const s of displaySlices) {
    options?.signal?.throwIfAborted();
    // Modern masks are sampled on the CT grid; only the legacy path uses storage indices.
    const physSliceIdx = s;
    if (physSliceIdx < 0 || physSliceIdx >= imageIds.length) continue;
    const plane = targetPlanes[s];
    const sampled = plane && sampleMask ? sampleMask(plane) : undefined;
    const planeRows = plane?.rows ?? nRows;
    const planeCols = plane?.columns ?? nCols;
    const readPlane = (r: number, c: number) =>
      sampled ? sampled[r * planeCols + c] : readVoxel(r, c, s);

    const segIndices = new Set<number>();
    for (let r = 0; r < planeRows; r++) {
      for (let c = 0; c < planeCols; c++) {
        if (readPlane(r, c) > 0) segIndices.add(readPlane(r, c));
      }
    }
    if (segIndices.size === 0) continue;

    // Compute origin offset from current slice to target slice.
    // vtkImageData.indexToWorld([c, r, 0]) uses currentOrigin (the displayed
    // slice). We shift to the target slice's IPP — but only the slice-normal
    // component, because the in-plane components are already encoded in (c, r)
    // for cor/sag MPR series (where the row direction runs along z, etc.).
    // Adding the full IPP delta unconditionally double-counts that
    // component on those views, which is what produced the legacy
    // mis-alignment for older annotations.
    const sliceIPM = cornerstone.metaData.get("imagePlaneModule", imageIds[physSliceIdx]);
    const targetIPP = sliceIPM?.imagePositionPatient;
    let ox = 0;
    let oy = 0;
    let oz = 0;
    // A stack draws each contour on its referenced image with that image's real
    // IPP, so its points go through the displayed (loaded) image plus the
    // normal offset, not the sampling plane: un-streamed slices may still carry
    // a synthesized IPP without the real in-plane origin (ENG-258).
    const placeOnDisplayedImage = !useSformAffine || isStack;
    if (placeOnDisplayedImage && targetIPP) {
      // Direction matrix: 9 floats column-major; the third column (indices
      // 6..8) is the unit slice-normal. vtk.js commonly returns a
      // Float64Array here, so check for an indexable collection of length 9
      // rather than `Array.isArray`. Falling back to axial would re-introduce
      // the mis-alignment this branch fixes on cor/sag — warn and skip.
      const rawDir = vtkImageData.getDirection?.() as ArrayLike<number> | undefined;
      if (!rawDir || rawDir.length !== 9) {
        console.warn(
          "[niftiMaskLoader] Skipping legacy slice — vtkImageData.getDirection unavailable",
          { sliceIndex: s, gotLength: rawDir?.length },
        );
        continue;
      }
      try {
        const delta: [number, number, number] = [
          targetIPP[0] - currentOrigin[0],
          targetIPP[1] - currentOrigin[1],
          targetIPP[2] - currentOrigin[2],
        ];
        [ox, oy, oz] = projectIppDeltaOntoNormal(delta, rawDir);
      } catch (err) {
        console.warn("[niftiMaskLoader] Skipping legacy slice — bad direction matrix", {
          sliceIndex: s,
          err,
        });
        continue;
      }
    }

    segIndices.forEach((segIdx) => {
      if (
        options?.includedStoredSegmentIndices &&
        !options.includedStoredSegmentIndices.has(segIdx)
      )
        return;
      const savedAnnotationId = options?.annotationIdsBySegment?.get(segIdx);
      const alreadyPresent =
        options?.skipExistingContours &&
        savedAnnotationId &&
        (annotationState.getAllAnnotations() ?? []).some(
          (annotation: any) =>
            annotation.data?.savedAnnotationId === savedAnnotationId &&
            annotation.metadata?.referencedImageId === imageIds[physSliceIdx] &&
            (annotation.data?.contour?.polyline?.length ?? 0) >= 3,
        );
      if (alreadyPresent) return;

      const sliceMask = new Uint8Array(planeRows * planeCols);
      for (let r = 0; r < planeRows; r++) {
        for (let c = 0; c < planeCols; c++) {
          if (readPlane(r, c) === segIdx) sliceMask[r * planeCols + c] = 1;
        }
      }

      // ENG-250: trace pixel EDGES, one outline per blob, so Save rasterizes
      // back exactly these voxels. The old centre trace (#724 needed an
      // un-cross pass for it) dropped the outer half-pixel ring, single-voxel
      // slices and every blob after the first.
      const outlines = extractContourEdges(sliceMask, planeCols, planeRows);
      outlines.forEach((outline, blob) => {
        // Use 2 smoothing iterations to match RegionGrowTool's default,
        // ensuring consistent contour quality between fresh and loaded annotations.
        const smoothed = chaikinSmooth(outline, 2);

        // Modern contours were extracted on this CT plane, so use its geometry
        // for world points as well as image references. Keep legacy conversion separate.
        const worldPoints: [number, number, number][] = smoothed.map(([r, c]) => {
          if (plane && !placeOnDisplayedImage) {
            return plane.pixelToWorld(c, r);
          }
          const wp = vtkImageData.indexToWorld([c, r, 0]);
          return [wp[0] + ox, wp[1] + oy, wp[2] + oz] as [number, number, number];
        });

        // Do NOT append duplicate closing point — closed: true handles closure

        // Bind every loaded contour to a deterministic, caller-stable id
        // (`getContourSegmentationId(segmentationId)`). The earlier "active at
        // time of write" approach was unstable: each VolumeViewport mount calls
        // ensureContourSegmentationActive with its own series' labelmap id,
        // overriding the global active mid-load — so annotations from earlier
        // loadMaskAsContours calls ended up bound to a now-orphan contour seg.
        // Using a single stable id means all contours land in the same bucket
        // regardless of mount order, and the matching ensureContourSegmentation
        // -Active call before/after the loop re-asserts it as active.
        const vpSegId = getContourSegmentationId(segmentationId);

        const uid = csUtils.uuidv4?.() ?? `mask-${s}-${segIdx}-${blob}-${Date.now()}`;
        const annotation = {
          annotationUID: uid,
          metadata: {
            toolName: "PlanarFreehandContourSegmentationTool",
            referencedImageId: imageIds[physSliceIdx],
            FrameOfReferenceUID: frameOfRef,
            // On a stack, the camera's, like contours drawn there.
            viewPlaneNormal: [...((!placeOnDisplayedImage && plane?.normal) || viewPlaneNormal)],
            viewUp: [...((!placeOnDisplayedImage && plane?.viewUp) || viewUp)],
            sliceIndex: physSliceIdx,
          },
          data: {
            savedAnnotationId: options?.annotationIdsBySegment?.get(segIdx),
            contour: { polyline: worldPoints, closed: true },
            segmentation: { segmentationId: vpSegId, segmentIndex: resolveLocalIndex(segIdx) },
            handles: {
              points: [],
              activeHandleIndex: null,
              textBox: {
                hasMoved: false,
                worldPosition: [0, 0, 0],
                worldBoundingBox: {
                  topLeft: [0, 0, 0],
                  topRight: [0, 0, 0],
                  bottomLeft: [0, 0, 0],
                  bottomRight: [0, 0, 0],
                },
              },
            },
          },
          highlighted: false,
          isLocked: true, // Mark as locked so save flow skips loaded contours
          isVisible: true,
          invalidated: false,
          [LOADED_FROM_MASK_KEY]: true,
        };

        annotationState.addAnnotation(annotation as any, element as any);
        // Register with segmentation contour representation so the renderer draws it.
        try {
          csTools.utilities.contourSegmentation.addContourSegmentationAnnotation(annotation as any);
        } catch (err) {
          console.error(
            "[niftiMaskLoader] addContourSegmentationAnnotation failed — contour will be invisible:",
            err,
          );
        }
        totalContours++;
      });
    });
  }

  // Re-assert active segmentation. Each VolumeViewport mount calls
  // ensureContourSegmentationActive with its own series' labelmap id, so the
  // active __contour can get clobbered between the early-load run above and
  // now. Calling it again pins the active to OUR canonical id (the one we
  // just bound all the annotations to), so the contour renderer walks the
  // segmentation that actually contains them.
  try {
    ensureContourSegmentationActive(segmentationId, activeSegmentBeforeLoad);
  } catch (err) {
    console.warn("[niftiMaskLoader] post-load ensureContourSegmentationActive failed:", err);
  }

  // Trigger re-render
  try {
    viewport.render();
  } catch {
    /* ignore */
  }

  return totalContours;
}
